/**
 * Admin — корневой компонент админ-панели
 * Layout с sidebar, верхней панелью, роутингом и toast-уведомлениями
 */

import React, { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useToast } from './hooks';
import { modalStyles } from './Modal';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import ChangePasswordPage from './ChangePasswordPage';

// Ленивая загрузка страниц для оптимизации
const Dashboard = React.lazy(() => import('./Dashboard'));
const ScheduleManager = React.lazy(() => import('./ScheduleManager'));
const RoutesManager = React.lazy(() => import('./RoutesManager'));
const BusesManager = React.lazy(() => import('./BusesManager'));

/** Fallback при загрузке страницы */
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Toast-контейнер — показывает уведомления в правом нижнем углу */
const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((toast) => {
        const bgColors = {
          success: 'bg-green-600',
          error: 'bg-red-600',
          info: 'bg-blue-600',
        };
        const icons = {
          success: '✓',
          error: '✕',
          info: 'ℹ',
        };

        return (
          <div
            key={toast.id}
            className={`${bgColors[toast.type]} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[280px] animate-slideInRight`}
          >
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
              {icons[toast.type]}
            </span>
            <span className="text-sm font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="flex-shrink-0 text-white/70 hover:text-white transition-colors"
              aria-label="Закрыть"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};

/** Заголовок страницы по пути */
const getPageTitle = (pathname: string): string => {
  switch (pathname) {
    case '/admin':
      return 'Dashboard';
    case '/admin/schedule':
      return 'Управление расписанием';
    case '/admin/routes':
      return 'Маршруты';
    case '/admin/buses':
      return 'Автобусы';
    default:
      return 'Admin';
  }
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  if (user.forcePasswordChange && location.pathname !== '/admin/change-password') {
    return <Navigate to="/admin/change-password" replace />;
  }

  return <>{children}</>;
};

const AdminShell: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const initials = (user?.displayName || user?.username || 'A')
    .split(' ')
    .map((part) => part[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* CSS анимации */}
      <style>{modalStyles}</style>
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .border-3 {
          border-width: 3px;
        }
      `}</style>

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Основной контент */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        }`}
      >
        {/* Верхняя панель */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            {getPageTitle(location.pathname)}
          </h1>
          <div className="flex items-center gap-3">
            {/* Индикатор подключения */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-green-700">API Online</span>
            </div>
            <button
              onClick={() => navigate('/admin/change-password')}
              className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              Сменить пароль
            </button>
            <button
              onClick={() => logout().then(() => navigate('/admin/login', { replace: true }))}
              className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
            >
              Выйти
            </button>
            <div className="flex items-center gap-3 rounded-full bg-slate-900 px-3 py-1.5 text-white shadow-sm">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                {initials || 'A'}
              </div>
              <div className="text-left">
                <div className="text-xs font-semibold">{user?.displayName || user?.username}</div>
                <div className="text-[11px] text-slate-300">{user?.roles.join(', ')}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Контент страницы */}
        <main className="p-6 max-w-7xl mx-auto">
          <React.Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/schedule" element={<ScheduleManager />} />
              <Route path="/routes" element={<RoutesManager />} />
              <Route path="/buses" element={<BusesManager />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </React.Suspense>
        </main>
      </div>

      {/* Toast-уведомления */}
      <ToastContainer />
    </div>
  );
};

const Admin: React.FC = () => (
  <AuthProvider>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AdminShell />
          </RequireAuth>
        }
      />
    </Routes>
  </AuthProvider>
);

export default Admin;
